import { supabase } from '@/lib/supabase';
import { SUPABASE_URL } from '@/config';
import { SeoCopilotSession, SeoCopilotCycle } from '@/types/api/seo';

function mapSession(row: any): SeoCopilotSession {
  return {
    id: row.id,
    projectId: row.project_id,
    keywordId: row.keyword_id,
    targetPosition: row.target_position,
    status: row.status,
    currentPosition: row.current_position,
    startingPosition: row.starting_position,
    bestPosition: row.best_position,
    totalCycles: row.total_cycles,
    maxCycles: row.max_cycles,
    lastCycleAt: row.last_cycle_at,
    nextCycleAt: row.next_cycle_at,
    completedAt: row.completed_at,
    createdBy: row.created_by,
    createdAt: row.created_at,
  };
}

function mapCycle(row: any): SeoCopilotCycle {
  return {
    id: row.id,
    sessionId: row.session_id,
    cycleNumber: row.cycle_number,
    positionBefore: row.position_before,
    ctrBefore: row.ctr_before,
    clicksBefore: row.clicks_before,
    impressionsBefore: row.impressions_before,
    opportunitiesFound: row.opportunities_found,
    selectedOpportunityType: row.selected_opportunity_type,
    selectedOpportunityTitle: row.selected_opportunity_title,
    diagnosisReasoning: row.diagnosis_reasoning,
    actionTaken: row.action_taken,
    actionDescription: row.action_description,
    appliedValue: row.applied_value,
    positionAfter: row.position_after,
    impactStatus: row.impact_status,
    status: row.status,
    errorMessage: row.error_message,
    createdAt: row.created_at,
  };
}

const seoCopilotService = {
  async getSessions(projectId: string): Promise<SeoCopilotSession[]> {
    const { data, error } = await supabase
      .from('seo_copilot_sessions')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapSession);
  },

  async getSessionForKeyword(keywordId: string): Promise<SeoCopilotSession | null> {
    const { data, error } = await supabase
      .from('seo_copilot_sessions')
      .select('*')
      .eq('keyword_id', keywordId)
      .in('status', ['active', 'paused'])
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw error;
    return data && data.length > 0 ? mapSession(data[0]) : null;
  },

  async getCycles(sessionId: string): Promise<SeoCopilotCycle[]> {
    const { data, error } = await supabase
      .from('seo_copilot_cycles')
      .select('*')
      .eq('session_id', sessionId)
      .order('cycle_number', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapCycle);
  },

  async startSession(
    projectId: string,
    keywordId: string,
    targetPosition = 3,
    maxCycles = 12,
    targetPageUrl?: string | null
  ): Promise<{
    sessionId: string;
    status: string;
    firstCycle: { status: string; action: string | null; description: string | null; diagnosis: string | null; opportunitiesFound: number } | null;
    tasks: Array<{ id: string; title: string; taskType: string; priority: string; status: string }>;
  }> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: { session } } = await supabase.auth.getSession();

    const res = await fetch(`${SUPABASE_URL}/functions/v1/seo-copilot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({
        action: 'startSession',
        projectId,
        keywordId,
        targetPosition,
        maxCycles,
        targetPageUrl: targetPageUrl ?? undefined,
        userId: user?.id,
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? 'Failed to start copilot');
    return { sessionId: data.sessionId, status: data.status, firstCycle: data.firstCycle ?? null, tasks: data.tasks ?? [] };
  },

  async pauseSession(sessionId: string): Promise<void> {
    const { error } = await supabase
      .from('seo_copilot_sessions')
      .update({ status: 'paused' })
      .eq('id', sessionId);

    if (error) throw error;
  },

  async resumeSession(sessionId: string): Promise<void> {
    const nextCycleAt = new Date();
    nextCycleAt.setDate(nextCycleAt.getDate() + 1);

    const { error } = await supabase
      .from('seo_copilot_sessions')
      .update({ status: 'active', next_cycle_at: nextCycleAt.toISOString() })
      .eq('id', sessionId);

    if (error) throw error;
  },

  async triggerCycle(sessionId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: { session } } = await supabase.auth.getSession();

    const res = await fetch(`${SUPABASE_URL}/functions/v1/seo-copilot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ action: 'triggerCycle', sessionId, userId: user?.id }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error ?? 'Failed to trigger cycle');
    }
  },
};

export default seoCopilotService;
